import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as playgroundService from '../services/playgroundService';
import useAuthStore from '../stores/useAuthStore';
import styles from './IconPicker.module.css';

const fileToBase64Payload = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const comma = result.indexOf(',');
      const base64 = comma >= 0 ? result.slice(comma + 1) : result;
      resolve({ base64, contentType: file.type || 'image/png' });
    };
    reader.onerror = () => reject(reader.error || new Error('Read failed'));
    reader.readAsDataURL(file);
  });

function urlsMatch(a, b) {
  if (a == null || b == null) return false;
  const s = String(a).trim();
  const t = String(b).trim();
  if (!s || !t) return false;
  try {
    return new URL(s).href === new URL(t).href;
  } catch {
    return s === t;
  }
}

function galleryItemMatchesSelection(icon, value) {
  const v = value || {};
  if (v.key && icon.key && v.key === icon.key) return true;
  if (v.url && icon.url && urlsMatch(v.url, icon.url)) return true;
  return false;
}

/**
 * @param {{ url?: string, key?: string, anchor?: string, imageFit?: string }} value
 * @param {(v: object) => void} onChange
 * @param {'icon'|'ad'} kind
 * @param {boolean} [showAnchor]
 * @param {boolean} [showImageFit] — sponsor banner small icon (centerAndFill)
 */
function IconPicker({ value = {}, onChange, kind, showAnchor = false, showImageFit = false }) {
  const getAuthHeaders = useAuthStore((s) => s.getAuthHeaders);
  const [tab, setTab] = useState('upload');
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryIcons, setGalleryIcons] = useState([]);
  const [uploading, setUploading] = useState(false);

  const merge = useCallback(
    (patch) => {
      onChange({ ...value, ...patch });
    },
    [onChange, value]
  );

  const loadGallery = useCallback(async () => {
    if (galleryIcons.length > 0) return;
    setGalleryLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const icons = await playgroundService.fetchAdminIcons(authHeaders);
      setGalleryIcons(Array.isArray(icons) ? icons : []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load icon gallery');
    } finally {
      setGalleryLoading(false);
    }
  }, [galleryIcons.length, getAuthHeaders]);

  useEffect(() => {
    const key = value?.key;
    const url = typeof value?.url === 'string' ? value.url.trim() : '';
    if (key) {
      setTab('gallery');
      loadGallery();
    } else if (url) {
      setTab('url');
    }
  }, [value?.key, value?.url, loadGallery]);

  const handleGalleryOpen = () => {
    setTab('gallery');
    loadGallery();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { base64, contentType } = await fileToBase64Payload(file);
      const authHeaders = await getAuthHeaders();
      const res = await playgroundService.uploadPublicAsset(
        { fileContent: base64, contentType, kind },
        authHeaders
      );
      if (res?.url) {
        merge({ url: res.url, key: res.key || value.key });
        toast.success('Icon uploaded');
      } else {
        toast.error('Upload did not return a URL');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const selectGalleryIcon = (icon) => {
    merge({ url: icon.url, key: icon.key });
    toast.success('Icon selected');
  };

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'upload' ? styles.tabActive : ''}`}
          onClick={() => setTab('upload')}
        >
          Upload
        </button>
        <button type="button" className={`${styles.tab} ${tab === 'gallery' ? styles.tabActive : ''}`} onClick={handleGalleryOpen}>
          Gallery
        </button>
        <button type="button" className={`${styles.tab} ${tab === 'url' ? styles.tabActive : ''}`} onClick={() => setTab('url')}>
          URL
        </button>
      </div>

      {tab === 'upload' && (
        <div>
          <input
            className={styles.fileInput}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
            onChange={handleFile}
            disabled={uploading}
          />
          {uploading && <p className={styles.meta}>Uploading…</p>}
        </div>
      )}

      {tab === 'gallery' && (
        <div>
          {galleryLoading && <p className={styles.meta}>Loading icons…</p>}
          {!galleryLoading && galleryIcons.length === 0 && <p className={styles.meta}>No icons found.</p>}
          <div className={styles.gallery}>
            {galleryIcons.map((icon) => (
              <button
                key={icon.key || icon.url}
                type="button"
                className={`${styles.galleryItem} ${galleryItemMatchesSelection(icon, value) ? styles.galleryItemSelected : ''}`}
                onClick={() => selectGalleryIcon(icon)}
                title={icon.key}
              >
                <img src={icon.url} alt="" className={styles.galleryThumb} />
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'url' && (
        <div className={styles.row}>
          <label className={styles.label} htmlFor="icon-picker-url">
            Image URL
          </label>
          <input
            id="icon-picker-url"
            type="url"
            className={styles.input}
            value={value.url || ''}
            onChange={(e) => merge({ url: e.target.value.trim(), key: undefined })}
            placeholder="https://…"
          />
        </div>
      )}

      {showAnchor && (
        <div className={styles.row}>
          <label className={styles.label} htmlFor="icon-anchor">
            Marker anchor
          </label>
          <select
            id="icon-anchor"
            className={styles.select}
            value={value.anchor || 'bottom'}
            onChange={(e) => merge({ anchor: e.target.value })}
          >
            <option value="bottom">bottom</option>
            <option value="center">center</option>
          </select>
        </div>
      )}

      {showImageFit && (
        <div className={styles.row}>
          <label className={styles.label} htmlFor="icon-image-fit">
            Image fit (banner)
          </label>
          <select
            id="icon-image-fit"
            className={styles.select}
            value={value.imageFit || ''}
            onChange={(e) => merge({ imageFit: e.target.value || undefined })}
          >
            <option value="">Default</option>
            <option value="centerAndFill">centerAndFill</option>
          </select>
        </div>
      )}

      {(value.url || value.key) && (
        <div className={styles.meta}>
          {value.key && <div>key: {value.key}</div>}
          {value.url && <div>url: {value.url}</div>}
          {value.url && (
            <button type="button" className={styles.clearBtn} onClick={() => onChange({})}>
              Clear icon
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default IconPicker;
